import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useUsage } from '../../services/api/queries/useUsage';

export function UsageWidget() {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const { data, isLoading } = useUsage();

  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  if (isLoading) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>{t('settings.usageTitle')}</Text>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  if (!data) return null;

  return (
    <View style={styles.container}>
      <Text style={styles.title}>{t('settings.usageTitle')}</Text>
      <Text style={styles.subtitle}>{t('settings.usageWindow', { days: data.window_days })}</Text>

      <View style={styles.statsRow}>
        <View style={styles.stat}>
          <Text style={styles.statValue}>${data.total_cost_usd.toFixed(4)}</Text>
          <Text style={styles.statLabel}>{t('settings.usageCost')}</Text>
        </View>
        <View style={styles.stat}>
          <Text style={styles.statValue}>{data.total_credits.toLocaleString()}</Text>
          <Text style={styles.statLabel}>{t('settings.usageCredits')}</Text>
        </View>
        <View style={styles.stat}>
          <Text style={styles.statValue}>{data.call_count.toLocaleString()}</Text>
          <Text style={styles.statLabel}>{t('settings.usageCalls')}</Text>
        </View>
      </View>

      {data.breakdown.length > 0 && (
        <View style={styles.breakdown}>
          {data.breakdown.map((row) => (
            <View key={row.source} style={styles.breakdownRow}>
              <Text style={styles.breakdownSource}>{row.source}</Text>
              <Text style={styles.breakdownDetail}>
                {row.call_count} calls · ${row.total_cost_usd.toFixed(4)}
              </Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: 16,
      marginBottom: 24,
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    title: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 4,
    },
    subtitle: {
      fontSize: 12,
      color: theme.colors.textSecondary,
      marginBottom: 12,
    },
    statsRow: {
      flexDirection: 'row',
      justifyContent: 'space-around',
      marginBottom: 12,
    },
    stat: {
      alignItems: 'center',
    },
    statValue: {
      fontSize: 18,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    statLabel: {
      fontSize: 12,
      color: theme.colors.textSecondary,
      marginTop: 2,
    },
    breakdown: {
      borderTopWidth: 1,
      borderTopColor: theme.colors.border,
      paddingTop: 8,
    },
    breakdownRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      paddingVertical: 4,
    },
    breakdownSource: {
      fontSize: 13,
      color: theme.colors.text,
      fontWeight: '500',
    },
    breakdownDetail: {
      fontSize: 13,
      color: theme.colors.textSecondary,
    },
  });
}
